export type StepType = 'demo' | 'pause' | 'handoff' | 'talk' | 'screen'

export interface DemoStep {
  action: string
  presenter?: string
  type: StepType
  notes?: string
}

export interface DemoSection {
  title: string
  steps: DemoStep[]
}

export interface ClientContext {
  terminology: Record<string, string>
  demo_data: string[]
  timing?: string
  notes?: string
}

export interface DemoRunsheet {
  client_context: ClientContext
  sections: DemoSection[]
  discovery_questions?: string[]
}

// Keep backward compat for existing plans that may have old format
export interface TalkingPoint {
  area: string
  points: string[]
  priority: 'high' | 'medium' | 'low'
}

export type DemoPlanStatus = 'draft' | 'generating' | 'complete' | 'error'

export const PROSPECT_INDUSTRIES = [
  { value: 'crypto', label: 'Crypto / Digital Assets' },
  { value: 'neobank', label: 'Neobank / Digital Bank' },
  { value: 'traditional_bank', label: 'Traditional Bank' },
  { value: 'lender', label: 'Lender / Credit Provider' },
  { value: 'investor_platform', label: 'Investor Platform / Wealth Management' },
  { value: 'payments', label: 'Payments / Remittance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'gaming', label: 'Gaming / Gambling' },
  { value: 'marketplace', label: 'Marketplace / Platform' },
  { value: 'tranche2', label: 'Tranche 2 Reporting Entity' },
  { value: 'bnpl', label: 'Buy Now Pay Later' },
  { value: 'superannuation', label: 'Superannuation / Pension' },
  { value: 'telco', label: 'Telco' },
  { value: 'real_estate', label: 'Real Estate / Conveyancing' },
  { value: 'legal', label: 'Legal / Accounting' },
  { value: 'other', label: 'Other' },
] as const

export type ProspectIndustry = typeof PROSPECT_INDUSTRIES[number]['value']

export interface DemoPrepPlan {
  id: number
  deal_id: number | null
  title: string
  prospect_industry: ProspectIndustry | null
  raw_input: string | null
  talking_points: DemoRunsheet | TalkingPoint[] | null
  status: DemoPlanStatus
  error_message: string | null
  created_by: string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
  customer_name?: string
}

export function isRunsheet(tp: DemoRunsheet | TalkingPoint[] | null): tp is DemoRunsheet {
  return tp !== null && !Array.isArray(tp) && 'sections' in tp
}

export const STATUS_CONFIG: Record<DemoPlanStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'text-gray-400' },
  generating: { label: 'Generating...', color: 'text-blue-400' },
  complete: { label: 'Complete', color: 'text-green-400' },
  error: { label: 'Error', color: 'text-red-400' },
}

export const STEP_TYPE_CONFIG: Record<StepType, { label: string; color: string; bg: string; icon: string }> = {
  demo: { label: 'Demo', color: 'text-violet-400', bg: 'bg-violet-500/10', icon: 'monitor' },
  talk: { label: 'Talk', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: 'message' },
  pause: { label: 'Pause', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: 'pause' },
  handoff: { label: 'Handoff', color: 'text-green-400', bg: 'bg-green-500/10', icon: 'arrow-right' },
  screen: { label: 'Screen', color: 'text-red-400', bg: 'bg-red-500/10', icon: 'monitor-off' },
}
